/**
 * Composant pour gérer la navigation entre les étapes du formulaire référent
 * Extrait de ReferentForm.tsx pour réduire la complexité
 */

import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { BrandedButton, BrandedProgressBar, BrandedStepIndicator, BrandedText } from '@/components/shared/ui';

interface BrandingSettings {
  enabled: boolean;
  logo_url?: string;
  primary_color?: string;
}

interface ReferentFormNavigationProps {
  currentStep: number;
  totalSteps: number;
  canGoNext: boolean;
  canGoPrevious: boolean;
  canSubmit?: boolean;
  isSubmitting: boolean;
  isLastStep: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onSubmit: () => void;
  branding?: BrandingSettings | null;
  onSignatureClick?: () => Promise<void>;
  isSignaturePending?: boolean;
}

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  stepTitles: string[];
  branding?: BrandingSettings | null;
}



// Composant pour l'indicateur de progression
export const StepIndicator = ({ currentStep, totalSteps, stepTitles, branding }: StepIndicatorProps) => {
  return (
    <div className="mb-8 font-['Bricolage_Grotesque']">
      {/* Barre de progression brandée */}
      <BrandedProgressBar 
        currentStep={currentStep}
        totalSteps={totalSteps}
        branding={branding}
      />
      
      {/* Indicateurs d'étapes brandés */}
      <BrandedStepIndicator 
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepTitles={stepTitles}
        branding={branding}
      />
    </div>
  );
};

// Composant pour les boutons de navigation
export const ReferentFormNavigation = ({
  currentStep,
  totalSteps,
  canGoNext,
  canGoPrevious,
  canSubmit,
  isSubmitting,
  isLastStep,
  onNext,
  onPrevious,
  onSubmit,
  branding,
  onSignatureClick,
  isSignaturePending
}: ReferentFormNavigationProps) => {
  return (
    <>
    <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 pt-6 border-t font-['Bricolage_Grotesque']">
      {/* Bouton Précédent */}
      <Button
        type="button"
        variant="outline"
        onClick={onPrevious}
        disabled={!canGoPrevious || isSubmitting}
        className="flex items-center justify-center gap-2 w-full sm:w-auto"
      >
        <ChevronLeft className="h-4 w-4" />
        Précédent
      </Button>

      {/* Informations sur l'étape */}
      <div className="text-sm text-gray-500 text-center order-first sm:order-none">
        Étape {currentStep} sur {totalSteps}
      </div>

      {/* Bouton Suivant/Soumettre */}
      {isLastStep ? (
        <BrandedButton
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || isSubmitting}
          className="flex items-center justify-center gap-2 w-full sm:w-auto whitespace-normal sm:whitespace-nowrap"
          branding={branding}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Envoi en cours...
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              Soumettre
            </>
          )}
        </BrandedButton>
      ) : currentStep === 2 ? (
        <BrandedButton
          type="button"
          onClick={onSignatureClick}
          disabled={isSignaturePending}
          className="flex items-center justify-center gap-2 w-full sm:w-auto whitespace-normal sm:whitespace-nowrap text-center px-4 py-2"
          branding={branding}
        >
          {isSignaturePending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              <span className="hidden sm:inline">Préparation du document...</span>
              <span className="sm:hidden">Préparation...</span>
            </>
          ) : (
            <>
              <span className="hidden sm:inline">Cliquez pour signer électroniquement</span>
              <span className="sm:hidden">Signer électroniquement</span>
            </>
          )}
        </BrandedButton>
      ) : (
        <BrandedButton
          type="button"
          onClick={onNext}
          disabled={!canGoNext || isSubmitting}
          className="flex items-center justify-center gap-2 w-full sm:w-auto"
          branding={branding}
        >
          {currentStep === 3 ? 'Continuer' : 'Suivant'}
          <ChevronRight className="h-4 w-4" />
        </BrandedButton>
      )}
    </div>

    {/* Footer RGPD */}
    <div className="flex flex-col justify-center items-center gap-4 mt-8 pt-6 border-t px-4">
      <div className="max-w-md text-center text-neutral-400 text-xs sm:text-sm font-normal font-['Bricolage_Grotesque'] leading-relaxed">
        Cette demande de référence est effectuée conformément à la législation en vigueur. Vos réponses sont confidentielles et traitées conformément au RGPD.
      </div>
      <BrandedText 
        className="text-center text-sm sm:text-base font-normal font-['Bricolage_Grotesque'] leading-tight"
        branding={branding}
        as="div"
      >
        L'équipe Getproof
      </BrandedText>
    </div>
    </>
  );
};

